import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

import { AnySourceData, Layer, Map, Marker, Popup } from 'mapbox-gl';

import { AddFilter, AddLayer } from '@/services/layer-add.service';
import { RemoveLayer, RemoveOnlyLayer } from '@/services/layer-remove.service';
import { MapLayer } from '@/models/map-layer.model';
import { MapConfig } from '@/models/map-config.model';
import { FeatureCollection, MultiPolygon } from '@turf/helpers';
import { setVisibilityIsMarkerInPolygon } from '@/services/map.service';
import { RemoveSource } from '@/services/source-remove.service';
import { MarkerDetailsModel } from '@/models/marker-details.model';

@Module({
  name: 'map',
  namespaced: true
})
export class MapStore extends VuexModule {
  isLoaded = false;
  showLoading = false;
  city = '';
  time = 600;
  popup: null | Popup = null;
  previousPopup: null | number = null;
  newHeight = 25;
  lastMarker: [number, number] | [] = [];
  _layers: { layer: Layer; type: string }[] = [];
  leftSideDrawer = true;
  internetLegend = false;
  internetFibreLegend = false;
  selectedPopup: null | Popup = null;
  selectedPopupEmploi: any = null;
  airQualityModal = false;
  greenSpacesModal = false;
  noiseLevelLegend = false;
  pointsOfInterestModal = false;
  pointsOfInterestPopup = false;
  restaurantsGitesGuestroomsShopsPopup = false;
  coworkingsPopup = false;
  hotelsPopup = false;
  activitiesPopup = false;
  recruitmentPoolWidget = false;
  mobilePopup: any = null;

  initConfig!: MapConfig;

  private _map!: Map;
  _filterPolygon: FeatureCollection<MultiPolygon> | null = null;

  private _sources: { source: AnySourceData; type: string }[] = [];
  private _markers: { marker: Marker; type: string }[] = [];

  private _cityMarkerComponents: MarkerDetailsModel[] = [];
  private _selectedCityMarkerId = '';

  private _travelType: 'car' | 'transit' | 'airplane' = 'transit';
  private _travelTypeInternational: 'transit' | 'airplane' = 'transit';

  get map() {
    return this._map;
  }

  get height() {
    return this.newHeight;
  }

  get lastMarkerCoord() {
    return this.lastMarker;
  }

  get layers() {
    return this._layers;
  }

  get cityMarkers() {
    return this._markers.filter(({ type }) => type === 'City').map(({ marker }) => marker);
  }

  get cityMarkerComponents() {
    return this._cityMarkerComponents;
  }

  get selectedCityMarkerId() {
    return this._selectedCityMarkerId;
  }

  get travelType() {
    return this._travelType;
  }

  @Mutation
  setNewHeight() {
    this.newHeight = this.newHeight + 2;
  }

  @Mutation
  setSelectedPopupEmploi(data: any) {
    this.selectedPopupEmploi = data;
  }

  @Mutation
  setRecruitmentPoolWidget(data: boolean) {
    this.recruitmentPoolWidget = data;
  }

  @Mutation
  setAirQualityModal(value: boolean) {
    this.airQualityModal = value;
  }

  @Mutation
  setGreenSpacesModal(value: boolean) {
    this.greenSpacesModal = value;
  }

  @Mutation
  setNoiseLevelLegend(value: boolean) {
    this.noiseLevelLegend = value;
  }

  @Mutation
  setPointsOfInterestModal(value: boolean) {
    this.pointsOfInterestModal = value;
  }

  @Mutation
  setMobilePopup(value: any) {
    this.mobilePopup = value;
  }

  @Mutation
  setPointsOfInterestPopup(value: any) {
    this.pointsOfInterestPopup = value;
  }

  @Mutation
  setRestaurantsGitesGuestroomsShopsPopup(value: any) {
    this.restaurantsGitesGuestroomsShopsPopup = value;
  }

  @Mutation
  setCoworkingsPopup(value: any) {
    this.coworkingsPopup = value;
  }

  @Mutation
  setHotelsPopup(value: any) {
    this.hotelsPopup = value;
  }

  @Mutation
  setActivitiesPopup(value: any) {
    this.activitiesPopup = value;
  }

  @Mutation
  setLeftSideDrawer(value: boolean) {
    this.leftSideDrawer = value;
  }

  @Mutation
  setSelectedPopup(value: any) {
    if (this.selectedPopup) this.selectedPopup.remove();
    this.selectedPopup = value;
    if (value) {
      this.selectedPopup.addTo(this._map);
    }
  }

  @Mutation
  setInternetLegend(value: boolean) {
    this.internetLegend = value;
  }

  @Mutation
  setInternetFibreLegend(value: boolean) {
    this.internetFibreLegend = value;
  }

  @Mutation
  setNewLastMarker(arr: [number, number] | []) {
    this.lastMarker = arr;
  }

  @Mutation
  setPopup(obj: { lngLat: null | [number, number]; value: null | string }) {
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
    if (obj.lngLat && obj.value) {
      this.popup = new Popup({ offset: 5, maxWidth: '500px', className: 'popuptest' })
        .setLngLat(obj.lngLat)
        .setHTML(obj.value);
      this.popup.addTo(this._map);
    }
  }

  @Mutation
  changePreviousPopup(value: null | number) {
    this.previousPopup = value;
  }

  @Mutation
  addMap(map: any) {
    this._map = map;

    this._map.once('style.load', () => (this.isLoaded = true));
  }

  @Mutation
  init(config: { center: [number, number]; zoom: number }) {
    // remove markers from map
    this._markers.forEach(({ marker }) => marker.remove());

    // remove marker from state
    this._markers = [];

    // remove layers from map
    this._layers.forEach(({ type }) => RemoveLayer(this._map, type));

    // remove layer from state
    this._layers = [];

    // remove layers from map
    this._sources.forEach(({ type }) => RemoveSource(this._map, type));

    // remove layer from state
    this._sources = [];

    this.initConfig = config;
    this._map.flyTo(config);
    this._filterPolygon = null;
  }

  @Mutation
  resetPosition() {
    this._map.flyTo(this.initConfig);
    this._filterPolygon = null;
  }

  @Mutation
  flyTo(lngLat: [number, number]) {
    this._map.flyTo({ center: lngLat });
  }

  @Mutation
  addMarker(payload: { marker: Marker; type: string }) {
    const { marker, type } = payload;
    this._markers.push({ marker, type });

    marker.addTo(this._map);

    if (this._filterPolygon) {
      setVisibilityIsMarkerInPolygon(marker, this._filterPolygon);
    }
  }

  @Mutation
  removeMarker({ type }: { type: string }): void {
    this._markers.filter(item => item.type === type).forEach(({ marker }) => marker.remove());

    this._markers = this._markers.filter(item => item.type !== type);
  }

  @Mutation
  addFilter({ type, geoJsonPolygon, city, dragMarker }: MapLayer) {
    this._layers = this._layers.filter(item => item.type !== type);

    AddFilter(this._map, type, geoJsonPolygon, this._layers);
    this._filterPolygon = geoJsonPolygon;
    this.city = city;
    if (!dragMarker)
      this._markers.forEach(({ marker }) => setVisibilityIsMarkerInPolygon(marker, geoJsonPolygon, this._layers));
  }

  @Mutation
  addLayer({ layer, type }: { layer: Layer; type: string }) {
    this._layers = this._layers.filter(item => item.type !== type);

    this._layers.push({ type, layer: { ...layer, id: type } });

    AddLayer(this._map, { ...layer, id: type });
  }

  @Mutation
  removeLayer({ type }: { type: string }): void {
    const layer = this._layers.find(item => item.type === type);

    if (!layer) return;

    RemoveLayer(this._map, type);
    RemoveLayer(this._map, type + 1);
    this._layers = this._layers.filter(item => item.type !== type);
  }

  @Mutation
  removeOnlyLayer({ type }: { type: string }): void {
    const layer = this._layers.find(item => item.type === type);

    if (!layer) return;

    RemoveOnlyLayer(this._map, type);
    this._layers = this._layers.filter(item => item.type !== type);
  }

  @Mutation
  updateLayers() {
    this._layers.forEach(({ layer }) => this._map.addLayer(layer));
  }

  @Mutation
  addSource({ source, type }: { source: AnySourceData; type: string }) {
    this._sources = this._sources.filter(item => item.type !== type);

    this._sources.push({ source, type });

    this._map.addSource(type, source);
  }

  @Mutation
  removeSource({ type }: { type: string }): void {
    const source = this._sources.find(item => item.type === type);

    if (!source) return;

    RemoveSource(this._map, type);
    this._sources = this._sources.filter(item => item.type !== type);
  }

  @Mutation
  updateSources() {
    this._sources.forEach(({ source, type }) => {
      this._map.addSource(type, source);
    });
  }

  @Action({ commit: 'addMap' })
  loadMap() {
    return new Map({
      container: 'map',
      style: process.env.VUE_APP_MAPBOX_STYLE,
      accessToken: process.env.VUE_APP_MAPBOX,
      pitch: 45
    });
  }

  @Mutation
  zoomIn() {
    this._map.zoomIn();
  }

  @Mutation
  zoomOut() {
    this._map.zoomOut();
  }

  @Mutation
  addImage({ id, image }: { id: string; image: { width: number; height: number; data: Uint8Array } }) {
    if (this._map.hasImage(id)) this._map.removeImage(id);
    this._map.addImage(id, image);
  }

  @Mutation
  setCityMarkerComponents(markers: MarkerDetailsModel[]) {
    this._cityMarkerComponents = markers;
  }

  @Mutation
  setSelectedCityMarkerId(id: string) {
    this._selectedCityMarkerId = id;
  }

  @Mutation
  setTravelType(type: 'car' | 'transit' | 'airplane') {
    this._travelType = type;
  }

  @Mutation
  setTime(time: number) {
    this.time = time;
  }

  @Mutation
  setTravelTypeInternational(type: 'transit' | 'airplane') {
    this._travelTypeInternational = type;
  }
  @Mutation
  setPaintProperty({ layerId, name, value }: { layerId: string; name: string; value: string }) {
    if (this._map.getLayer(layerId)) this._map.setPaintProperty(layerId, name, value);
  }
  @Mutation
  setLoading(value: boolean) {
    this.showLoading = value;
  }
}
